import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getAppCategories } from "./appsConfig";
import type { AppCategory } from "./types";
import useIsMonitor from "../../../hooks/useIsMonitor";
import { isCategoryEnabled, isAppEnabled } from "../../../config/appsConfig";
import { useUserStore } from "../../../store/userStore";
import {
  PTAR_COMPANY_ID,
  getPtarAllowedApps,
} from "../../../hooks/useTruckYardRole";
import { WEB_SERVICE_ROLE_ID } from "../../../hooks/useIsWebServiceRole";

// Note: App IDs used for filtering:
// - "truck-yard" for Patio de Maniobras
// - "inspecciones" for Inspecciones

// Categories allowed for PTAR users (only "Gestión de Flota")
// Hide: "administracion", "internos", "point-of-sale"
const PTAR_ALLOWED_CATEGORIES = ["rastreo-telemetria"];

// Apps exclusive to PTAR company - block for all other users
const PTAR_EXCLUSIVE_APPS = ["truck-yard", "inspecciones"];

const ADMIN_RESELLER_ROLE_ID = "Ne6pwRmFkr4bArJN47NZ";

const MONITOR_RESTRICTED_CATEGORY = "rastreo-telemetria";
const MONITOR_HIDDEN_APPS = ["admin", "torre-control", "truck-yard"];
const MONITOR_HIDDEN_CATEGORIES = ["administracion", "point-of-sale"];

export function useFilteredCategories(): AppCategory[] {
  const { t } = useTranslation("common");
  const { isMonitor } = useIsMonitor();
  const user = useUserStore((state) => state.user);

  // Use user store data (reactive) with localStorage fallback
  const userRoleId = user?.role_id || localStorage.getItem("userRoleId") || "";
  const userCompanyId =
    user?.company_id ||
    localStorage.getItem("userCompanyId") ||
    localStorage.getItem("companyId") ||
    "";
  const canUseSG = user?.canUseSG === true;

  const categories = getAppCategories(t);

  const filteredCategories = useMemo(() => {
    // 1. Filtrar por app config (enabled flags from config.json)
    let filtered = categories
      .filter((category) => isCategoryEnabled(category.id))
      .map((category) => ({
        ...category,
        apps: category.apps.filter((app) => isAppEnabled(category.id, app.id)),
      }))
      .filter((category) => category.apps.length > 0);

    // 1.b. La Papelera (borrado definitivo / restaurar) solo es visible para
    // admin o admin_reseller, sin importar la categoría donde se muestre.
    const roleName = (localStorage.getItem("userRole") || "").toLowerCase();
    const canSeePapelera =
      roleName === "admin" || userRoleId === ADMIN_RESELLER_ROLE_ID;
    if (!canSeePapelera) {
      filtered = filtered
        .map((category) => ({
          ...category,
          apps: category.apps.filter((app) => app.id !== "papelera"),
        }))
        .filter((category) => category.apps.length > 0);
    }

    if (!canUseSG) {
      const canUseSGApps = ["sg", "dashboards", "service-desk"];
      filtered = filtered
        .map((category) => ({
          ...category,
          apps: category.apps.filter((app) => !canUseSGApps.includes(app.id)),
        }))
        .filter((category) => category.apps.length > 0);
    }

    // 2. Filtrar por roles
    filtered = filtered.filter((category) => {
      if (!category.requiredRoleIds || category.requiredRoleIds.length === 0) {
        return true;
      }
      return category.requiredRoleIds.includes(userRoleId);
    });

    // 2.b. Rol web service: solo Mapa (lite), Administración de Flotilla
    // (admin, acotada a Notificaciones) e Integradores de flota
    if (userRoleId === WEB_SERVICE_ROLE_ID) {
      const allowed = ["lite", "admin", "fleet-integradores"];
      return filtered
        .map((category) => ({
          ...category,
          apps: category.apps.filter((app) => allowed.includes(app.id)),
        }))
        .filter((category) => category.apps.length > 0);
    }

    // 3. Si es monitor, aplicar filtros adicionales
    if (isMonitor) {
      filtered = filtered
        .map((category) =>
          category.id === MONITOR_RESTRICTED_CATEGORY
            ? {
                ...category,
                apps: category.apps.filter(
                  (app) => !MONITOR_HIDDEN_APPS.includes(app.id),
                ),
              }
            : category,
        )
        .filter((category) => !MONITOR_HIDDEN_CATEGORIES.includes(category.id))
        .filter((category) => category.apps.length > 0);
    }

    // 4. Filtrar por roles de PTAR (admin de compañía, admin de patios,
    //    supervisor, inspector, vigilante).
    //
    // La cadena de `if/else if` sobre los cuatro ids que había acá es el
    // mecanismo de #558: un rol de PTAR fuera de la cadena no entraba ni en
    // esta rama ni en el `else if (!isPTARCompany)` de abajo, así que salía sin
    // NINGÚN recorte — con Facturación, POS y Ajustes a la vista, que el filtro
    // le saca hasta al admin de compañía. La tabla de useTruckYardRole es
    // ahora el único lugar donde se declara qué apps lleva cada rol.
    const isPTARCompany = userCompanyId === PTAR_COMPANY_ID;
    const allowedApps = getPtarAllowedApps(userCompanyId, userRoleId);

    // Only apply PTAR restrictions if user belongs to PTAR company
    if (allowedApps) {
      // First filter by allowed categories (only "Gestión de Flota")
      // Then filter apps within those categories
      filtered = filtered
        .filter((category) => PTAR_ALLOWED_CATEGORIES.includes(category.id))
        .map((category) => ({
          ...category,
          apps: category.apps.filter((app) => allowedApps.includes(app.id)),
        }))
        .filter((category) => category.apps.length > 0);
    } else if (!isPTARCompany) {
      // Block PTAR exclusive apps (truck-yard, inspecciones) for non-PTAR users
      // Exception: Admin Reseller has access to all apps including PTAR exclusive apps
      const isAdminReseller = userRoleId === ADMIN_RESELLER_ROLE_ID;

      filtered = filtered
        .map((category) => ({
          ...category,
          apps: category.apps.filter(
            (app) => isAdminReseller || !PTAR_EXCLUSIVE_APPS.includes(app.id),
          ),
        }))
        .filter((category) => category.apps.length > 0);
    }

    return filtered;
  }, [userRoleId, userCompanyId, categories, isMonitor, user, canUseSG]);

  return filteredCategories;
}

export { ADMIN_RESELLER_ROLE_ID };
